import type { UseFormSetValue } from 'react-hook-form';
import type { CompanyLookupCompany } from '@/lib/api';
import type { RegistrationFormValues } from '@/hooks/useRegistrationForm';

/**
 * Apply a company-lookup result to the RHF form state. Pure — does not
 * read the form, only writes via `setValue`. The wizard form is a
 * shared FormProvider context, so writes from step 1 (organizzazione)
 * propagate into step 2 (contatti, where PEC lives) and step 3
 * (indirizzi, where sede legale lives).
 *
 * Behaviour rules:
 *   - Every write uses `{ shouldDirty: true, shouldTouch: true,
 *     shouldValidate: false }`. Validation is left to the user's
 *     "Continua" click — pre-validating prefilled fields would surface
 *     errors the user did not yet have a chance to fix.
 *   - Empty / undefined provider fields are SKIPPED (not written),
 *     so the user's manually-typed value is preserved when the
 *     provider has no answer for that field.
 *   - `street` is a single FE input; the BE `streetNumber` is
 *     concatenated into it (`Via Roma 12`) so the FE shape stays
 *     unchanged.
 *
 * No fields are LOCKED: the user can edit anything after prefill —
 * this matches the "human reviews provider data" UX requirement.
 */
export function applyCompanyLookupPrefill(
  setValue: UseFormSetValue<RegistrationFormValues>,
  company: CompanyLookupCompany,
): void {
  const opts = { shouldDirty: true, shouldTouch: true, shouldValidate: false };

  if (company.legalName && company.legalName.trim().length > 0) {
    setValue('organization.legalName', company.legalName.trim(), opts);
  }
  if (company.vatNumber && company.vatNumber.trim().length > 0) {
    // BE VAT regex strips non-digits; we mirror it here so the form
    // value matches what the schema will accept on validation.
    setValue(
      'organization.vatNumber',
      company.vatNumber.replace(/\D/g, ''),
      opts,
    );
  }
  if (company.fiscalCode && company.fiscalCode.trim().length > 0) {
    setValue(
      'organization.fiscalCode',
      company.fiscalCode.trim().toUpperCase(),
      opts,
    );
  }
  if (company.pecEmail && company.pecEmail.trim().length > 0) {
    // PEC lives on `orgContacts.pec` (PR-C), gets emitted at submit
    // time as a `contactChannels[type=PEC]` row.
    setValue('orgContacts.pec', company.pecEmail.trim(), opts);
  }
  if (company.sdiCode && company.sdiCode.trim().length > 0) {
    setValue('organization.sdiCode', company.sdiCode.trim().toUpperCase(), opts);
  }
  if (
    company.businessLegalForm &&
    company.businessLegalForm.trim().length > 0
  ) {
    // The BE only fills this when the provider's legal-form code
    // mapped to one of the closed `BUSINESS_LEGAL_FORMS` values.
    // No SelectField fallback needed — empty string keeps the field
    // unset and the user picks manually.
    setValue(
      'organization.businessLegalForm',
      company.businessLegalForm,
      opts,
    );
  }

  const addr = company.legalAddress;
  if (addr) {
    if (addr.street || addr.streetNumber) {
      const street = [addr.street?.trim(), addr.streetNumber?.trim()]
        .filter((v) => v && v.length > 0)
        .join(' ');
      if (street.length > 0) {
        setValue('addresses.primary.street', street, opts);
      }
    }
    if (addr.city && addr.city.trim().length > 0) {
      setValue('addresses.primary.city', addr.city.trim(), opts);
    }
    if (addr.province && addr.province.trim().length > 0) {
      setValue(
        'addresses.primary.province',
        addr.province.trim().toUpperCase(),
        opts,
      );
    }
    if (addr.zip && addr.zip.trim().length > 0) {
      setValue('addresses.primary.zip', addr.zip.trim(), opts);
    }
    if (addr.country && addr.country.trim().length > 0) {
      setValue(
        'addresses.primary.country',
        addr.country.trim().toUpperCase(),
        opts,
      );
    }
  }
}

/**
 * Italian copy for the soft-failure messages shown in the dialog. Kept
 * here so the dialog component stays focused on rendering.
 */
export const COMPANY_LOOKUP_UNAVAILABLE_LABELS: Record<string, string> = {
  NOT_FOUND: 'Nessuna azienda trovata con questo codice. Compila i dati manualmente.',
  PROVIDER_NOT_CONFIGURED:
    'Servizio di precompilazione non disponibile in questo ambiente. Compila i dati manualmente.',
  PROVIDER_TIMEOUT:
    'Il servizio di precompilazione non risponde. Riprova o compila i dati manualmente.',
  PROVIDER_RATE_LIMIT:
    'Troppe richieste in poco tempo. Aspetta qualche minuto o compila manualmente.',
  PROVIDER_UNAVAILABLE:
    'Servizio di precompilazione momentaneamente non raggiungibile. Compila i dati manualmente.',
};
